import { useCallback, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '../lib/gamification';
import { 
  ArrowLeft, Bell, Plus, Check, Trash2, Calendar, Clock, AlertTriangle,
  CheckCircle2, Circle, Loader2, CalendarClock, FileText, CreditCard, Building2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryConfig = {
  general: { icon: Bell, color: 'text-gray-400', bg: 'bg-gray-500/20', label: 'Général' },
  impots: { icon: CreditCard, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Impôts' },
  aides: { icon: FileText, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Aides' },
  documents: { icon: Building2, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Documents' },
  abonnements: { icon: CalendarClock, color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Abonnements' }
};

export const RappelsPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  
  const [reminders, setReminders] = useState([]);
  const [stats, setStats] = useState({ total: 0, upcoming: 0, overdue: 0, completed: 0 });
  const [grouped, setGrouped] = useState({ upcoming: [], overdue: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: '',
    description: '',
    due_date: '',
    category: 'general'
  });

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data.reminders || []);
      setStats(response.data.stats || {});
      setGrouped(response.data.grouped || {});
    } catch (error) {
      console.error('Error loading reminders:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadReminders();
  }, [token, loadReminders]);

  const handleCreate = async () => {
    if (!newReminder.title || !newReminder.due_date) {
      toast.error('Titre et date requis');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/reminders`,
        {
          ...newReminder,
          due_date: new Date(newReminder.due_date).toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rappel créé');
      setAddDialogOpen(false);
      setNewReminder({ title: '', description: '', due_date: '', category: 'general' });
      loadReminders();
      trackEvent(token, 'first_reminder');
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleToggleComplete = async (reminder) => {
    try {
      await axios.patch(
        `${API_URL}/api/reminders/${reminder.id}`,
        { completed: !reminder.completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadReminders();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/reminders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rappel supprimé');
      loadReminders();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSeedFiscal = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/reminders/seed-fiscal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`${response.data.count} rappels fiscaux créés`);
        loadReminders();
        trackEvent(token, 'fiscal_ready');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const getDaysRemaining = (dateStr) => {
    const now = new Date();
    const due = new Date(dateStr);
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const ReminderCard = ({ reminder, showActions = true }) => {
    const cat = categoryConfig[reminder.category] || categoryConfig.general;
    const Icon = cat.icon;
    const daysRemaining = getDaysRemaining(reminder.due_date);
    const isOverdue = daysRemaining < 0 && !reminder.completed;
    const isUrgent = daysRemaining <= 3 && daysRemaining >= 0 && !reminder.completed;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-4 rounded-xl border transition-all ${
          reminder.completed
            ? isDark ? 'bg-white/5 border-white/5 opacity-60' : 'bg-gray-50 border-gray-100 opacity-60'
            : isOverdue
              ? isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
              : isUrgent
                ? isDark ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
                : isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-start gap-3">
          {showActions && (
            <button
              onClick={() => handleToggleComplete(reminder)}
              className={`mt-0.5 transition-colors ${
                reminder.completed 
                  ? 'text-green-500' 
                  : isDark ? 'text-[#64748B] hover:text-[#81d8d0]' : 'text-gray-400 hover:text-[#81d8d0]'
              }`}
            >
              {reminder.completed ? <CheckCircle2 size={20} /> : <Circle size={20} />}
            </button>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className={`font-medium ${reminder.completed ? 'line-through' : ''} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {reminder.title}
              </h4>
              <span className={`px-2 py-0.5 rounded text-xs ${cat.bg} ${cat.color}`}>
                {cat.label}
              </span>
              {isOverdue && !reminder.completed && (
                <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">
                  En retard
                </span>
              )}
              {isUrgent && (
                <span className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400">
                  Urgent
                </span>
              )}
            </div>
            
            {reminder.description && (
              <p className={`text-sm mt-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                {reminder.description}
              </p>
            )}
            
            <div className={`flex items-center gap-4 mt-2 text-xs ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {formatDate(reminder.due_date)}
              </span>
              {!reminder.completed && (
                <span className={`flex items-center gap-1 ${
                  isOverdue ? 'text-red-400' : isUrgent ? 'text-orange-400' : ''
                }`}>
                  <Clock size={12} />
                  {daysRemaining === 0 ? "Aujourd'hui" : 
                   daysRemaining === 1 ? "Demain" :
                   daysRemaining < 0 ? `${Math.abs(daysRemaining)}j de retard` :
                   `Dans ${daysRemaining}j`}
                </span>
              )}
            </div>
          </div>
          
          {showActions && (
            <button
              onClick={() => handleDelete(reminder.id)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'
              }`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className={`min-h-screen px-4 py-6 transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="rappels-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Link 
          to="/dashboard" 
          className={`inline-flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-[#81d8d0]/20 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-[#81d8d0]" />
                </div>
                <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Rappels
                </h1>
              </div>
              <p className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>
                Ne rate plus jamais une deadline administrative
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSeedFiscal}
                className={isDark ? 'border-white/20' : ''}
              >
                <CalendarClock size={16} className="mr-2" />
                Ajouter calendrier fiscal
              </Button>
              <Button
                onClick={() => setAddDialogOpen(true)}
                className="bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A]"
              >
                <Plus size={16} className="mr-2" />
                Nouveau
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <Card className={`p-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Total</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
          </Card>
          <Card className={`p-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>À venir</p>
            <p className="text-2xl font-bold text-[#81d8d0]">{stats.upcoming}</p>
          </Card>
          <Card className={`p-4 border ${stats.overdue > 0 ? 'border-red-500/30' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>En retard</p>
            <p className={`text-2xl font-bold ${stats.overdue > 0 ? 'text-red-400' : isDark ? 'text-white' : 'text-gray-900'}`}>
              {stats.overdue}
            </p>
          </Card>
          <Card className={`p-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Terminés</p>
            <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
          </Card>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#81d8d0]" />
          </div>
        ) : reminders.length === 0 ? (
          <Card className={`p-12 text-center border border-dashed ${
            isDark ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-300'
          }`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              isDark ? 'bg-[#81d8d0]/10' : 'bg-[#81d8d0]/20'
            }`}>
              <Bell className="w-8 h-8 text-[#81d8d0]" />
            </div>
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Aucun rappel
            </h3>
            <p className={`mb-6 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
              Crée ton premier rappel ou ajoute le calendrier fiscal 2026
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={handleSeedFiscal}
                className={isDark ? 'border-white/20' : ''}
              >
                Ajouter calendrier fiscal
              </Button>
              <Button
                onClick={() => setAddDialogOpen(true)}
                className="bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A]"
              >
                <Plus size={16} className="mr-2" />
                Créer un rappel
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Overdue */}
            {grouped.overdue?.length > 0 && (
              <div>
                <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <AlertTriangle size={20} className="text-red-400" />
                  En retard ({grouped.overdue.length})
                </h3>
                <div className="space-y-3">
                  {grouped.overdue.map(reminder => (
                    <ReminderCard key={reminder.id} reminder={reminder} />
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming */}
            {grouped.upcoming?.length > 0 && (
              <div>
                <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Clock size={20} className="text-[#81d8d0]" />
                  À venir ({grouped.upcoming.length})
                </h3>
                <div className="space-y-3">
                  {grouped.upcoming.map(reminder => (
                    <ReminderCard key={reminder.id} reminder={reminder} />
                  ))}
                </div>
              </div>
            )}

            {/* Completed */}
            {grouped.completed?.length > 0 && (
              <div>
                <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <CheckCircle2 size={20} className="text-green-400" />
                  Terminés ({stats.completed})
                </h3>
                <div className="space-y-3">
                  {grouped.completed.map(reminder => (
                    <ReminderCard key={reminder.id} reminder={reminder} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className={isDark ? 'bg-[#1A1A2E] border-white/10' : 'bg-white'}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
                Nouveau rappel
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Titre *</Label>
                <Input
                  value={newReminder.title}
                  onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  placeholder="Ex: Déclaration de revenus"
                />
              </div>
              
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Description</Label>
                <Input
                  value={newReminder.description}
                  onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  placeholder="Détails optionnels..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Date limite *</Label>
                  <Input
                    type="datetime-local"
                    value={newReminder.due_date}
                    onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })}
                    className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  />
                </div>
                
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Catégorie</Label>
                  <Select value={newReminder.category} onValueChange={(v) => setNewReminder({ ...newReminder, category: v })}>
                    <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categoryConfig).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button
                onClick={handleCreate}
                disabled={!newReminder.title || !newReminder.due_date}
                className="w-full bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A]"
              >
                Créer le rappel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RappelsPage;
